import {
  SegmentedButtons,
  Typography,
  SelectMenu,
  SelectMenuOption,
  Icon,
  Button,
  TextField,
} from '@scale/scaleui';
import { useCallback } from 'react';
import {
  ConditionalBase,
  ConditionalVisibility,
  ModelResponseSelectorBasedConditionalVisibility,
  TextCollectionBasedConditionalVisibility,
  ConditionalOperator,
  OperatorParam,
  StepParamsForType,
  MultiTurnInteractionConfiguration,
} from '@scale/shared/types/chat/params';
import { ReviewLevel } from '@scale/shared/defaults';
import { TextCollectionBasedConditionalConfig } from './TextCollectionConfig';

import clsx from 'clsx';
import produce from 'immer';
import _ from 'lodash';

const CONDITIONAL_SOURCES = ['ModelResponseSelector', 'TextCollection'];

enum VisibilityOption {
  None = 'none',
  Restricted = 'restricted',
}

type StepConfigProps = {
  currentParams: StepParamsForType<any>;
  previousParams: any[];
  setCurrentParams: (value: any) => void;
  taskParams?: MultiTurnInteractionConfiguration;
};

function getDefaultParamsForModelResponseSelectorBased(
  conditionalBaseParams: any,
): ModelResponseSelectorBasedConditionalVisibility {
  const ids: string[] = conditionalBaseParams.params.chat_models.map(
    (modelDefinition: any) => modelDefinition.id as string,
  );
  if (!ids.length)
    return {
      id: conditionalBaseParams.id,
      type: conditionalBaseParams.type,
      params: {},
    };
  return {
    id: conditionalBaseParams.id,
    type: conditionalBaseParams.type,
    params: {
      modelId: ids[0],
    },
  };
}

function getDefaultParamsForTextCollectionBased(
  conditionalBaseParams: any,
): TextCollectionBasedConditionalVisibility {
  return {
    id: conditionalBaseParams.id,
    type: conditionalBaseParams.type,
    params: {
      fields: {
        type: 'AND',
        params: [],
      },
    },
  };
}

function getDefaultConditionalConfigForBase(conditionalBaseParams: any) {
  switch (conditionalBaseParams.type) {
    case 'ModelResponseSelector':
      return getDefaultParamsForModelResponseSelectorBased(conditionalBaseParams);
    case 'TextCollection':
      return getDefaultParamsForTextCollectionBased(conditionalBaseParams);

    default:
      throw new Error(`Unsupported conditional source type: ${conditionalBaseParams.type}`);
  }
}

function RuleCard({
  title,
  onDelete,
  negated = false,
  onNegatedChange,
  children,
}: {
  title?: string;
  onDelete?: () => void;
  negated?: boolean;
  onNegatedChange?(): void;
  children: React.ReactNode;
}) {
  return (
    <div
      className={clsx(
        'w-full bg-white border shadow-sm rounded-lg py-4 px-3 relative',
        negated ? 'border-danger-500' : 'border-neutral-300',
      )}
    >
      {title && <div>{title}</div>}
      <div className="flex items-center absolute top-1 right-1">
        {onNegatedChange && (
          <Button variant={negated ? 'primary' : 'secondary'} onClick={onNegatedChange}>
            Negate
          </Button>
        )}
        {onDelete && <Icon onClick={onDelete} variant="danger" icon={['fas', 'times']} />}
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function ConditionalVisibilityComponent({
  operator,
  depth,
  onChange,
  onDelete,
  previousParams,
  negated,
  onNegatedChange,
  taskParams,
}: {
  operator: OperatorParam;
  depth: number;
  onDelete?(): void;
  onChange(value: any): void;
  negated?: boolean;
  onNegatedChange?(): void;
  taskParams?: MultiTurnInteractionConfiguration;
} & Pick<StepConfigProps, 'previousParams'>) {
  if (operator.type === 'NOT') {
    return (
      <ConditionalVisibilityComponent
        previousParams={previousParams}
        operator={operator.params}
        depth={depth + 1}
        onChange={value =>
          onChange(
            produce(operator, draft => {
              draft.params = value;
            }),
          )
        }
        onDelete={onDelete}
        onNegatedChange={() => onChange(operator.params)}
        negated
        taskParams={taskParams}
      />
    );
  }
  if (
    operator.type === 'PromptTextCollection' ||
    operator.type === 'ResponseTextCollection' ||
    operator.type === 'ThoughtTextCollection' ||
    operator.type === 'ThoughtCorrection' ||
    operator.type === 'TextCollectionPerResponse' ||
    operator.type === 'QuantitativeModelResponseSelector' ||
    operator.type === 'AudioTextCollection'
  ) {
    return <p>Unsupported conditional source</p>;
  }

  // Only base operations are OR, so if we have an OR operator, we are at the root
  const isRoot = depth === 0;

  // Only enable negating AND and OR operators if they have more than one child
  const canNegate =
    (operator.type !== 'AND' && operator.type !== 'OR') || operator.params.length > 1;

  return (
    <RuleCard
      negated={negated}
      onNegatedChange={
        canNegate
          ? () => {
              if (onNegatedChange) {
                onNegatedChange();
              } else {
                onChange({
                  type: 'NOT',
                  params: operator,
                });
              }
            }
          : undefined
      }
      onDelete={onDelete}
    >
      {operator.type === 'ModelResponseSelector' || operator.type === 'TextCollection' ? (
        <div className="flex flex-col gap-2">
          <ConditionalBaseSelector
            operator={operator}
            onChange={id => {
              const conditionalBaseParams = previousParams.find(params => params.id === id);
              const conditionalVisibility =
                getDefaultConditionalConfigForBase(conditionalBaseParams);

              onChange(conditionalVisibility);
            }}
            previousParams={previousParams}
          />
          {operator.type === 'ModelResponseSelector' ? (
            <ConditionalSecondarySelector
              operator={operator}
              previousParams={previousParams}
              onChange={params => {
                onChange(
                  produce(operator, draft => {
                    draft.params = params;
                  }),
                );
              }}
            />
          ) : (
            <TextCollectionBasedConditionalConfig
              operator={operator}
              previousParams={previousParams}
              onChange={updatedOperator => onChange(updatedOperator)}
            />
          )}
        </div>
      ) : operator.type === 'Turn' ? (
        <TurnSelector
          operator={operator}
          onChange={value => {
            onChange(
              produce(operator, draft => {
                draft.params = value;
              }),
            );
          }}
          maxTurns={_.isNumber(taskParams?.maxTurns) ? Number(taskParams?.maxTurns) : 10}
        />
      ) : operator.type === 'TemplateVariable' ? (
        <TemplateVariableSelector
          operator={operator}
          onChange={value => {
            onChange(
              produce(operator, draft => {
                draft.params = value;
              }),
            );
          }}
          templateVariables={taskParams?.templateVariables}
        />
      ) : operator.type === 'ReviewLevel' ? (
        <ReviewLevelSelector
          operator={operator}
          onChange={value => {
            onChange(
              produce(operator, draft => {
                draft.params = value;
              }),
            );
          }}
          reviewLevels={[
            ReviewLevel.Attempt,
            ReviewLevel.Normal,
            ReviewLevel.Level1,
            ReviewLevel.ReviewConsensus,
            ReviewLevel.Expedite,
            ReviewLevel.Corp,
            ReviewLevel.CorpFlagged,
            ReviewLevel.Deliverable,
          ]}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {operator.params.map((op, index) => {
            return (
              <>
                <ConditionalVisibilityComponent
                  onDelete={() => {
                    onChange(
                      produce(operator, draft => {
                        draft.params.splice(index, 1);
                      }),
                    );
                  }}
                  onChange={conditional_visibility => {
                    onChange(
                      produce(operator, draft => {
                        draft.params[index] = conditional_visibility;
                      }),
                    );
                  }}
                  operator={op as ConditionalOperator}
                  previousParams={previousParams}
                  depth={depth + 1}
                  taskParams={taskParams}
                />
                {index !== operator.params.length - 1 && (
                  <div className="flex items-center justify-center gap-4 text-sm font-semibold">
                    {operator.type}
                  </div>
                )}
              </>
            );
          })}

          <SelectMenu<string>
            label="Add Condition"
            setSelected={id => {
              onChange(
                produce(operator, draft => {
                  let params: OperatorParam;
                  if (id === 'AND') {
                    params = { type: 'AND', params: [] };
                  } else if (id === 'turn-condition') {
                    params = {
                      id: 'turn-condition',
                      type: 'Turn',
                      params: { turns: [1] },
                    };
                  } else if (id === 'template-variable-condition') {
                    params = {
                      id: 'template-variable',
                      type: 'TemplateVariable',
                      params: { variable: '', values: [''] },
                    };
                  } else if (id === 'review-level-condition') {
                    params = {
                      id: 'review-level',
                      type: 'ReviewLevel',
                      params: { reviewLevels: [] },
                    };
                  } else {
                    const conditionalBaseParams = previousParams.find(params => params.id === id);
                    params = getDefaultConditionalConfigForBase(conditionalBaseParams);
                  }

                  // If we are at the root and the selected option is not AND, wrap it in an AND first
                  if (id !== 'AND' && isRoot) {
                    params = { type: 'AND', params: [params] };
                  }

                  draft.params.push(params);
                }),
              );
            }}
            options={[
              ...(!isRoot
                ? [
                    { id: '__conditionals', name: 'Conditionals', isHeader: true },
                    { id: 'AND', name: 'AND' },
                  ]
                : []),
              { id: '__previous_params', name: 'Previous Params', isHeader: true },
              ...previousParams
                .filter(params => CONDITIONAL_SOURCES.includes(params.type))
                .map(params => ({
                  id: params.id,
                  name: `(#${params.id}) ${params.type}`,
                })),
              { id: '__turn_based', name: 'Turn Conditions', isHeader: true },
              { id: 'turn-condition', name: 'Turn Equals' },
              { id: '__template_variables', name: 'Template Variables', isHeader: true },
              { id: 'template-variable-condition', name: 'Template Variable Equals' },
              { id: '__review_level', name: 'Review Level', isHeader: true },
              { id: 'review-level-condition', name: 'Review Level Equals' },
            ]}
            selectedOptionId={''}
          />
        </div>
      )}
    </RuleCard>
  );
}

function TemplateVariableSelector({
  operator,
  onChange,
  templateVariables = {},
}: {
  operator: any;
  onChange(value: any): void;
  templateVariables?: Record<string, string>;
}) {
  // select menu for the keys of template variables
  // text field for the values, separated by commas

  const keys = Object.keys(templateVariables);

  if (!keys.length) {
    return <p>No template variables available</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <SelectMenu<string>
        label="Variable Name"
        setSelected={key => onChange({ ...operator.params, variable: key })}
        options={keys.map(key => ({
          id: key,
          name: key,
        }))}
        selectedOptionId={operator.params.variable}
      />
      <TextField
        label="Included Values"
        placeholder='Enter values separated by commas, e.g. "value1, value2"'
        type="text"
        value={operator.params.values.join(',')}
        onChange={e => {
          const values = e.target.value.split(',');
          onChange({ ...operator.params, values });
        }}
      />
    </div>
  );
}

function TurnSelector({
  operator,
  onChange,
  maxTurns,
}: {
  operator: any;
  onChange(value: any): void;
  maxTurns: number;
}) {
  const turnArray = _.range(1, maxTurns + 1);
  return (
    <div className="flex flex-col gap-4">
      <SelectMenu<number>
        label="Included Turns"
        setSelected={value => {
          onChange({ turns: value });
        }}
        options={turnArray.map(turn => ({
          id: turn,
          name: turn.toString(),
        }))}
        multiple
        selectedOptionId={operator.params.turns}
      />
    </div>
  );
}

function ReviewLevelSelector({
  operator,
  onChange,
  reviewLevels,
}: {
  operator: any;
  onChange(value: any): void;
  reviewLevels: ReviewLevel[];
}) {
  return (
    <div className="flex flex-col gap-4">
      <SelectMenu<ReviewLevel>
        label="Included Review Levels"
        setSelected={value => {
          onChange({ reviewLevels: value });
        }}
        options={reviewLevels.map(level => ({
          id: level,
          name: level.toString(),
        }))}
        multiple
        selectedOptionId={operator.params.reviewLevels}
      />
    </div>
  );
}
interface ConditionalBaseSelectorProps {
  onChange: (id: string) => void;
  previousParams: any[];
  operator: ConditionalBase;
}

function ConditionalBaseSelector({
  operator,
  previousParams,
  onChange,
}: ConditionalBaseSelectorProps) {
  const conditionalBases = previousParams
    .filter(params => CONDITIONAL_SOURCES.includes(params.type))
    .map(params => ({
      id: params.id,
      name: `(#${params.id}) ${params.type}`,
    }));

  return (
    <SelectMenu<string>
      label="Conditional Base"
      setSelected={onChange}
      options={conditionalBases}
      selectedOptionId={operator.id}
    />
  );
}

function ConditionalSecondarySelector({
  operator,
  previousParams,
  onChange,
}: {
  operator: ConditionalBase;
  onChange(value: any): void;
  previousParams: any[];
}) {
  const conditionalBaseParams = previousParams
    .filter(params => CONDITIONAL_SOURCES.includes(params.type))
    .find(params => params.id === operator.id);

  const conditionalBaseConfig = operator as ModelResponseSelectorBasedConditionalVisibility;

  const ids: SelectMenuOption<string>[] = conditionalBaseParams
    ? conditionalBaseParams.params.chat_models.map((modelDefinition: any) => ({
        id: modelDefinition.id as string,
        name: modelDefinition.id as string,
      }))
    : [];

  if (!ids.length) return <p>ModelResponseSelector doesn't have models.</p>;

  const selectModelId = (id: string) => {
    onChange({ modelId: id });
  };

  return (
    <SelectMenu
      label="Selected Model ID"
      options={ids}
      selectedOptionId={conditionalBaseConfig?.params?.modelId ?? ids[0].id}
      setSelected={selectModelId}
    />
  );
}

export function ConditionalStepConfig(props: StepConfigProps) {
  const { previousParams, currentParams, setCurrentParams } = props;

  const setVisibilityOption = useCallback(
    (id: VisibilityOption) => {
      const params = currentParams.params;
      const newParams = { ...params };

      if (id === VisibilityOption.None && params.conditional_visibility) {
        delete newParams.conditional_visibility;
        setCurrentParams({ ...currentParams, params: newParams });
        return;
      }
      if (id === VisibilityOption.Restricted && !params.conditional_visibility) {
        const conditionalBaseParams = previousParams
          .filter(params => CONDITIONAL_SOURCES.includes(params.type))
          .at(-1);

        const conditional_visibility: ConditionalVisibility = {
          type: 'OR',
          params: [
            {
              type: 'AND',
              params: conditionalBaseParams
                ? [getDefaultConditionalConfigForBase(conditionalBaseParams)]
                : [],
            },
          ],
        };
        setCurrentParams({
          ...currentParams,
          params: {
            conditional_visibility,
            ...newParams,
          },
        });
      }
    },
    [currentParams, setCurrentParams, previousParams],
  );

  return (
    <div className="flex flex-col gap-4 mb-4">
      <Typography.Subtitle2>Conditional Visibility</Typography.Subtitle2>

      <div className="flex gap-4">
        <SegmentedButtons
          buttons={[
            {
              icon: ['far', 'users'],
              id: VisibilityOption.None,
              label: 'Always visible',
            },
            {
              icon: ['far', 'user-edit'],
              id: VisibilityOption.Restricted,
              label: 'Restrict visibility',
            },
          ]}
          onSelect={id => setVisibilityOption(id as VisibilityOption)}
          selected={
            currentParams.params.conditional_visibility
              ? VisibilityOption.Restricted
              : VisibilityOption.None
          }
        />
      </div>

      {currentParams.params.conditional_visibility ? (
        <div className="flex flex-col gap-4">
          <div>
            <ConditionalVisibilityComponent
              operator={currentParams.params.conditional_visibility}
              depth={0}
              onChange={value => {
                setCurrentParams({
                  ...currentParams,
                  params: {
                    ...currentParams.params,
                    conditional_visibility: value,
                  },
                });
              }}
              {...props}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
